import { useMutation, useQuery } from '@apollo/client';
import {
  adminInfoStoreQuery,
  productCategories as productCategoriesQuery,
} from '../../graphql/queries';
import React, { useState } from 'react';
import { updateStore, updateStoreAvailability } from '../../graphql/mutations';
import TimeSpanPickerMulti from '../../components/TimeSpanPickerMulti';
import SpecialOpeningHoursPicker from '../../components/SpecialOpeningHoursPicker';
import { getObjectDiff } from '@/utils/objDiff';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import moment from 'moment';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../components/Checkbox/pickpackCheckbox';
import { Checkbox } from 'primereact/checkbox';
import { ContentSection } from '../../components/Layout/ContentSection';

type MerchantOpeningHoursTabProps = {
  externalPickpackId: string | null;
  title: string;
};

const OutOfStockUntilType = {
  TIMEOUT: 'TIMEOUT',
  TOMORROW: 'TOMORROW',
  REACTIVATE: 'REACTIVATE',
};

export const MerchantOpeningHoursTab: React.FC<
  MerchantOpeningHoursTabProps
> = ({ externalPickpackId, title }) => {
  const [storeUpdate, setStoreUpdate] = useState<any | undefined>(null);
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>(null);
  const [updateStoreMutation, { loading: updating }] = useMutation(updateStore);
  const [updateAvailabilityMutation, { loading: updatingAvailability }] =
    useMutation(updateStoreAvailability);
  const [availibility, setAvailibility] = useState({
    type: OutOfStockUntilType.REACTIVATE,
    minutes: String(0),
  });

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useQuery(
    adminInfoStoreQuery,
    {
      fetchPolicy: 'no-cache',
      skip: !externalPickpackId,
      variables: { id: externalPickpackId },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData) {
          resetStore(storeData.store);
          const { store } = storeData;
          if (store != null) {
            // @ts-ignore
            const { offline_timeout: timeoutEnd } = store;
            if (timeoutEnd != null) {
              // @ts-ignore
              var duration = moment.duration(moment(timeoutEnd).diff(moment()));
              var minutes = duration.asMinutes();
              if (minutes > 0) {
                setAvailibility({
                  type: OutOfStockUntilType.TIMEOUT,
                  minutes: String(Math.round(minutes)),
                });
              }
            }
          }
        }
      },
    },
  );

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  function handleChange({ target }: { target: any }) {
    const { name, value } = target;

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      newStoreUpdate[name] = value;
      return newStoreUpdate;
    });
  }

  function handleAvailibilityChange({ target }: { target: any }) {
    let { name, value } = target;
    if (target.hasAttribute && target.hasAttribute('data-isnumber')) {
      value = value.replace(/[^0-9]/g, '');
    }
    setAvailibility((oldAvailibility: any) => {
      let newAvailibility = { ...oldAvailibility };
      newAvailibility[name] = value;
      return newAvailibility;
    });
  }

  async function setStoreAvailability() {
    let variables: any = {};
    if (availibility.type === OutOfStockUntilType.REACTIVATE) {
      variables = {
        store_id: store.id,
        online: true,
        type: OutOfStockUntilType.TIMEOUT,
        minutes: 0,
      };
    } else {
      variables = {
        store_id: store.id,
        online: false,
        type: availibility.type,
        minutes: parseInt(availibility.minutes, 10),
      };
    }
    const res = await updateAvailabilityMutation({ variables });
    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      newStoreUpdate.offline_timeout =
        res.data.updateStoreAvailability.offline_timeout;
      return newStoreUpdate;
    });
  }

  function handleSave() {
    setStoreAvailability();
    const getMutationInput = (storeIn: any) => {
      const lonlat = {
        longitude: Number(storeIn.lonlat.longitude),
        latitude: Number(storeIn.lonlat.latitude),
      };
      const mutationInput = {
        name: storeIn.name,
        address: {
          ...storeIn.address,
          __typename: undefined,
        },
        lonlat,
        opening_hours_multi: storeIn.opening_hours_multi_raw.map(
          (d: object) => ({
            ...d,
            __typename: undefined,
          }),
        ),
        delivery_hours: (storeIn.delivery_hours_raw || []).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        preorder_hours: (storeIn.preorder_hours_raw || []).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        special_opening_hours_multi: (
          storeIn.special_opening_hours_multi || []
        ).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        special_delivery_hours: (storeIn.special_delivery_hours || []).map(
          (d: object) => ({
            ...d,
            __typename: undefined,
          }),
        ),
        min_delivery_order_value: Number(storeIn.min_delivery_order_value),
        min_delivery_time: Number(storeIn.min_delivery_time),
        delivery_fee: Number(storeIn.delivery_fee),
        description_de: storeIn.description_de,
        description_en: storeIn.description_en,
        food_info_de: storeIn.description_de, // the fields food_info_(de|en) can be removed once the legacy App is not longer used
        food_info_en: storeIn.description_en,
        banner: storeIn.banner,
        phone_number: storeIn.phone_number,
        website: storeIn.website,
        min_order_time: Number(storeIn.min_order_time),
        cancelation_time: Number(storeIn.cancelation_time),
        preorder_sms_number: storeIn.preorder_sms_number,
        manual_overwrite_store_info: storeIn.manual_overwrite_store_info,
      };
      return mutationInput;
    };

    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput', mutationInput);
    updateStoreMutation({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (cache, { data: { updateStore: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store: ' + err);
    });
  }

  function getLastUpdatedAtString() {
    if (storeUpdate.last_google_opening_hours_sync != null) {
      return i18n.t('OpeningHours:lastUpdateAt', {
        lastUpdate: new Date(
          storeUpdate.last_google_opening_hours_sync,
        ).toLocaleDateString(
          i18n.language,
          getDateFormatOptions(false, i18n.language),
        ),
      });
    }
    return '';
  }

  if (!externalPickpackId) return <p>externalPickpackId is not avaliable</p>;

  if (!store) return <p>Store {externalPickpackId} could not be loaded</p>;

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={i18n.t('Common:openingHours')}
        onSave={handleSave}
        isLoading={updating}
      >
        {storeUpdate.google_places_id != null && (
          <EditItem title={i18n.t('OpeningHours:googleSync')}>
            <p>
              {`${i18n.t(
                'OpeningHours:googleSyncInfo',
              )} ${getLastUpdatedAtString()}`}{' '}
              <a
                href={`https://www.google.com/maps/place/?q=place_id:${storeUpdate.google_places_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.t('OpeningHours:googleMapsLink')}
              </a>
            </p>
            <CheckBoxLabelContainer
              onClick={() =>
                handleChange({
                  target: {
                    value: !storeUpdate.manual_overwrite_store_info,
                    name: 'manual_overwrite_store_info',
                  },
                })
              }
            >
              <Checkbox checked={storeUpdate.manual_overwrite_store_info} />
              <CheckBoxLabel>
                {i18n.t('OpeningHours:disbaleSync')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
          </EditItem>
        )}
        <EditItem title={i18n.t('OpeningHours:regularOpeningHours')}>
          {storeUpdate.can_preorder &&
            storeUpdate.preorder_hours_raw.length === 0 && (
              <div className="alert alert-warning mb-4" role="alert">
                {i18n.t('OpeningHours:preorderWarning')}
              </div>
            )}
          <TimeSpanPickerMulti
            disabled={
              storeUpdate.google_places_id != null &&
              !storeUpdate.manual_overwrite_store_info
            }
            openingHoursMulti={storeUpdate.opening_hours_multi_raw}
            onChange={(newOpeningHours: any) => {
              setStoreUpdate((oldStore: object) => ({
                ...oldStore,
                opening_hours_multi_raw: newOpeningHours,
              }));
            }}
          />
        </EditItem>
        {storeUpdate.can_preorder && (
          <EditItem title={i18n.t('OpeningHours:preorderHours')}>
            <CheckBoxLabelContainer
              onClick={() =>
                handleChange({
                  target: {
                    value:
                      storeUpdate.preorder_hours_raw.length > 0
                        ? []
                        : storeUpdate.opening_hours_multi_raw,
                    name: 'preorder_hours_raw',
                  },
                })
              }
            >
              <Checkbox checked={storeUpdate.preorder_hours_raw.length === 0} />
              <CheckBoxLabel>
                {i18n.t('OpeningHours:preorderHoursSame')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <br></br>
            <div className="alert alert-warning mb-4" role="alert">
              {i18n.t('OpeningHours:preorderWarning2')}
            </div>
            {storeUpdate.preorder_hours_raw.length > 0 && (
              <TimeSpanPickerMulti
                disabled={false}
                openingHoursMulti={storeUpdate.preorder_hours_raw}
                onChange={(newPreorderHours: any) => {
                  setStoreUpdate((oldStore: object) => ({
                    ...oldStore,
                    preorder_hours_raw: newPreorderHours,
                  }));
                }}
              />
            )}
          </EditItem>
        )}
        {storeUpdate.can_deliver && (
          <EditItem title={i18n.t('OpeningHours:deliveryTimes')}>
            <TimeSpanPickerMulti
              disabled={false}
              openingHoursMulti={storeUpdate.delivery_hours_raw}
              onChange={(newDeliveryHours: any) => {
                setStoreUpdate((oldStore: object) => ({
                  ...oldStore,
                  delivery_hours_raw: newDeliveryHours,
                }));
              }}
            />
          </EditItem>
        )}
        {(storeUpdate.can_deliver || storeUpdate.can_preorder) && (
          <EditItem title={i18n.t('OpeningHours:disableDeliveryTemporary')}>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('OpeningHours:currentlyDisabledUntil')}
              </span>
              <span className="form-control">
                {(storeUpdate.offline_timeout &&
                  moment(storeUpdate.offline_timeout).isAfter(moment()) &&
                  new Date(storeUpdate.offline_timeout).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )) ||
                  i18n.t('Common:no')}
              </span>
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('OpeningHours:disableUntil')}
              </span>
              <select
                className="form-control"
                name="type"
                value={availibility.type}
                onChange={handleAvailibilityChange}
              >
                <option
                  value={OutOfStockUntilType.REACTIVATE}
                  label={i18n.t('OpeningHours:reactivate')}
                />
                <option
                  value={OutOfStockUntilType.TIMEOUT}
                  label={i18n.t('OpeningHours:timeout')}
                />
                <option
                  value={OutOfStockUntilType.TOMORROW}
                  label={i18n.t('OpeningHours:tomorrow')}
                />
              </select>
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('OpeningHours:disableFor')}
              </span>
              <input
                data-isnumber
                className="form-control"
                disabled={availibility.type !== OutOfStockUntilType.TIMEOUT}
                name="minutes"
                value={availibility.minutes || ''}
                onChange={(e) => handleAvailibilityChange(e)}
              />
              <div className="input-group-apend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OpeningHours:minutes')}
                </span>
              </div>
            </div>
          </EditItem>
        )}
        <EditItem title={i18n.t('OpeningHours:specialHours')}>
          <SpecialOpeningHoursPicker
            specialOpeningHoursMulti={storeUpdate.special_opening_hours_multi}
            specialDeliveryHoursMulti={storeUpdate.special_delivery_hours}
            onChange={(newSpecialHours: any, isOpeningHours: boolean) => {
              let update = {};
              if (isOpeningHours) {
                update = {
                  special_opening_hours_multi: newSpecialHours,
                };
              } else {
                update = { special_delivery_hours: newSpecialHours };
              }

              setStoreUpdate((oldStore: object) => ({
                ...oldStore,
                ...update,
              }));
            }}
          />
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
